import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChartBarIcon,
  CalendarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FunnelIcon,
  DocumentTextIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { adminAPI } from '../services/api';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';

const Usage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [usageStats, setUsageStats] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);

  // Date range state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Filter and sort state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('total_cost');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showOnlyActive, setShowOnlyActive] = useState(false);

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // User detail modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTransactions, setUserTransactions] = useState([]);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);

  // Initialize dates
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  // Check admin status on mount
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const currentUser = await adminAPI.getCurrentUser();
        if (!currentUser.is_staff) {
          toast.error('Access denied. Admin privileges required.');
          navigate('/');
          return;
        }
        setIsAdmin(true);
      } catch (error) {
        console.error('Error checking admin status:', error);
        toast.error('Failed to verify admin status');
        navigate('/');
      }
    };

    checkAdmin();
  }, [navigate]);

  // Fetch data when dates change or on initial load
  useEffect(() => {
    if (isAdmin && startDate && endDate) {
      fetchData();
    }
  }, [isAdmin, startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersData, statsData] = await Promise.all([
        adminAPI.getAllUsers(),
        adminAPI.getAllUserUsageStats({ startDate, endDate })
      ]);

      setUsers(usersData);
      setUsageStats(statsData.data || {});
    } catch (error) {
      console.error('Error fetching usage data:', error);
      toast.error('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const stats = Object.values(usageStats);
    const totalCost = stats.reduce((sum, s) => sum + (s.total_cost || 0), 0);
    const totalSaved = stats.reduce((sum, s) => sum + (s.total_cost_saved || 0), 0);
    const totalEnrichments = stats.reduce((sum, s) => sum + (s.total_enrichments || 0), 0);
    const activeUsers = stats.filter(s => s.total_enrichments > 0).length;

    return {
      totalCost,
      totalSaved,
      totalEnrichments,
      activeUsers,
      totalUsers: users.length,
      savingsPercentage: totalCost + totalSaved > 0
        ? ((totalSaved / (totalCost + totalSaved)) * 100).toFixed(1)
        : 0
    };
  }, [usageStats, users]);

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let result = users.map(user => ({
      ...user,
      stats: usageStats[user.username] || {
        total_enrichments: 0,
        total_cost: 0,
        total_cost_saved: 0
      }
    }));

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(user =>
        user.username.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search)
      );
    }

    // Filter by activity
    if (showOnlyActive) {
      result = result.filter(user => user.stats.total_enrichments > 0);
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'username':
          aVal = a.username.toLowerCase();
          bVal = b.username.toLowerCase();
          break;
        case 'total_enrichments':
          aVal = a.stats.total_enrichments;
          bVal = b.stats.total_enrichments;
          break;
        case 'total_cost':
          aVal = a.stats.total_cost;
          bVal = b.stats.total_cost;
          break;
        case 'total_cost_saved':
          aVal = a.stats.total_cost_saved;
          bVal = b.stats.total_cost_saved;
          break;
        default:
          aVal = a.stats.total_cost;
          bVal = b.stats.total_cost;
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return result;
  }, [users, usageStats, searchTerm, sortField, sortDirection, showOnlyActive]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      const loadingToast = toast.loading('Generating Excel report...');

      // Fetch raw transactions
      const transactionsData = await adminAPI.getUsageTransactions({
        startDate,
        endDate,
        userIds: selectedUsers.length > 0 ? selectedUsers : null
      });

      const transactions = transactionsData.transactions || [];

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // ===== SHEET 1: User Summary =====
      const userSummaryHeaders = [
        'Username',
        'Email',
        'Total Enrichments',
        'API Cost',
        'Cache Saved'
      ];

      const userSummaryRows = [userSummaryHeaders];

      // Build user summary from filtered users
      const usersToExport = selectedUsers.length > 0
        ? filteredAndSortedUsers.filter(u => selectedUsers.includes(u.id))
        : filteredAndSortedUsers;

      let grandTotalEnrichments = 0;
      let grandTotalCost = 0;
      let grandTotalSaved = 0;

      usersToExport.forEach(user => {
        grandTotalEnrichments += user.stats.total_enrichments || 0;
        grandTotalCost += user.stats.total_cost || 0;
        grandTotalSaved += user.stats.total_cost_saved || 0;

        userSummaryRows.push([
          user.username,
          user.email || '',
          user.stats.total_enrichments || 0,
          `$${(user.stats.total_cost || 0).toFixed(2)}`,
          `$${(user.stats.total_cost_saved || 0).toFixed(2)}`
        ]);
      });

      // Add totals row
      userSummaryRows.push([]); // Empty row
      userSummaryRows.push([
        'TOTAL',
        '',
        grandTotalEnrichments,
        `$${grandTotalCost.toFixed(2)}`,
        `$${grandTotalSaved.toFixed(2)}`
      ]);

      const userSheet = XLSX.utils.aoa_to_sheet(userSummaryRows);
      userSheet['!cols'] = [
        { wch: 20 }, // Username
        { wch: 30 }, // Email
        { wch: 18 }, // Total Enrichments
        { wch: 12 }, // API Cost
        { wch: 12 }  // Cache Saved
      ];
      XLSX.utils.book_append_sheet(workbook, userSheet, 'User Summary');

      // ===== SHEET 2: Raw Transactions =====
      const transactionHeaders = [
        'Date',
        'Time',
        'Username',
        'Email',
        'Project',
        'API Cost',
        'Cache Saved'
      ];

      const transactionRows = [transactionHeaders];

      transactions.forEach(txn => {
        transactionRows.push([
          txn.date,
          txn.time,
          txn.username,
          txn.email,
          txn.project_name,
          `$${(txn.cost || 0).toFixed(2)}`,
          `$${(txn.cost_saved || 0).toFixed(2)}`
        ]);
      });

      const transactionSheet = XLSX.utils.aoa_to_sheet(transactionRows);
      transactionSheet['!cols'] = [
        { wch: 12 }, // Date
        { wch: 10 }, // Time
        { wch: 20 }, // Username
        { wch: 30 }, // Email
        { wch: 25 }, // Project
        { wch: 12 }, // API Cost
        { wch: 12 }  // Cache Saved
      ];
      XLSX.utils.book_append_sheet(workbook, transactionSheet, 'Raw Transactions');

      // Generate and download
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const dateRange = `${startDate}_to_${endDate}`;
      saveAs(blob, `usage_report_${dateRange}.xlsx`);

      toast.dismiss(loadingToast);
      toast.success(`Exported ${usersToExport.length} users and ${transactions.length} transactions`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export usage report');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsExporting(true);
      const loadingToast = toast.loading('Generating PDF report...');

      // Create PDF document
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      // Title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Usage Analytics Report', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      // Date range
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Period: ${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Summary Section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Summary', 14, yPos);
      yPos += 8;

      // Draw summary box
      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(249, 250, 251);
      doc.roundedRect(14, yPos - 2, pageWidth - 28, 30, 3, 3, 'FD');

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');

      const col1X = 20;
      const col2X = pageWidth / 2 + 10;

      doc.text(`Total API Cost: ${formatCurrency(summaryStats.totalCost)}`, col1X, yPos + 6);
      doc.text(`Cache Savings: ${formatCurrency(summaryStats.totalSaved)}`, col2X, yPos + 6);
      doc.text(`Total Enrichments: ${formatNumber(summaryStats.totalEnrichments)}`, col1X, yPos + 14);
      doc.text(`Active Users: ${summaryStats.activeUsers} of ${summaryStats.totalUsers}`, col2X, yPos + 14);

      yPos += 40;

      // User Breakdown Section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('User Breakdown', 14, yPos);
      yPos += 8;

      // Table header
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setFillColor(243, 244, 246);
      doc.rect(14, yPos - 4, pageWidth - 28, 8, 'F');

      const tableHeaders = ['User', 'Enrichments', 'API Cost', 'Cache Saved'];
      const colWidths = [60, 35, 40, 40];
      let xPos = 16;

      tableHeaders.forEach((header, i) => {
        doc.text(header, xPos, yPos);
        xPos += colWidths[i];
      });

      yPos += 6;

      // Table rows
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);

      const usersToExport = selectedUsers.length > 0
        ? filteredAndSortedUsers.filter(u => selectedUsers.includes(u.id))
        : filteredAndSortedUsers.filter(u => u.stats.total_enrichments > 0);

      usersToExport.forEach((user, index) => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }

        // Alternate row background
        if (index % 2 === 0) {
          doc.setFillColor(249, 250, 251);
          doc.rect(14, yPos - 4, pageWidth - 28, 7, 'F');
        }

        xPos = 16;
        doc.text(user.username.substring(0, 25), xPos, yPos);
        xPos += colWidths[0];
        doc.text(formatNumber(user.stats.total_enrichments), xPos, yPos);
        xPos += colWidths[1];
        doc.text(formatCurrency(user.stats.total_cost), xPos, yPos);
        xPos += colWidths[2];
        doc.text(formatCurrency(user.stats.total_cost_saved), xPos, yPos);

        yPos += 7;
      });

      // Footer
      yPos = doc.internal.pageSize.getHeight() - 15;
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Generated on ${new Date().toLocaleString()}`, 14, yPos);
      doc.text('TCG Navigator - Usage Report', pageWidth - 14, yPos, { align: 'right' });

      // Save PDF
      const dateRange = `${startDate}_to_${endDate}`;
      doc.save(`usage_report_${dateRange}.pdf`);

      toast.dismiss(loadingToast);
      toast.success('PDF report generated successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to generate PDF report');
    } finally {
      setIsExporting(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredAndSortedUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredAndSortedUsers.map(u => u.id));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <ChevronUpIcon className="h-4 w-4 inline ml-1" />
      : <ChevronDownIcon className="h-4 w-4 inline ml-1" />;
  };

  // Handle clicking on a user to view their enrichment details
  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setLoadingUserDetail(true);
    try {
      const transactionsData = await adminAPI.getUsageTransactions({
        startDate,
        endDate,
        userIds: [user.id]
      });
      setUserTransactions(transactionsData.transactions || []);
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      toast.error('Failed to load user enrichment details');
      setUserTransactions([]);
    } finally {
      setLoadingUserDetail(false);
    }
  };

  const closeUserDetail = () => {
    setSelectedUser(null);
    setUserTransactions([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading usage data...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ChartBarIcon className="h-8 w-8 text-orange-500" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Usage Analytics
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Monitor enrichment usage and costs across all users
                  {startDate && endDate && (
                    <span className="ml-2 text-orange-600 dark:text-orange-400 font-medium">
                      • {new Date(startDate).toLocaleDateString()} - {new Date(endDate).toLocaleDateString()}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Date Range Selection */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <CalendarIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Date Range</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <div className="flex items-end col-span-2">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    const today = new Date();
                    const weekAgo = new Date();
                    weekAgo.setDate(today.getDate() - 7);
                    setStartDate(weekAgo.toISOString().split('T')[0]);
                    setEndDate(today.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  7 Days
                </button>
                <button
                  onClick={() => {
                    const today = new Date();
                    const monthAgo = new Date();
                    monthAgo.setDate(today.getDate() - 30);
                    setStartDate(monthAgo.toISOString().split('T')[0]);
                    setEndDate(today.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  30 Days
                </button>
                <button
                  onClick={() => {
                    const today = new Date();
                    const sixMonthsAgo = new Date();
                    sixMonthsAgo.setMonth(today.getMonth() - 6);
                    setStartDate(sixMonthsAgo.toISOString().split('T')[0]);
                    setEndDate(today.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  6 Months
                </button>
                <button
                  onClick={() => {
                    const today = new Date();
                    const twelveMonthsAgo = new Date();
                    twelveMonthsAgo.setFullYear(today.getFullYear() - 1);
                    setStartDate(twelveMonthsAgo.toISOString().split('T')[0]);
                    setEndDate(today.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  12 Months
                </button>
                <button
                  onClick={() => {
                    const today = new Date();
                    const yearStart = new Date(today.getFullYear(), 0, 1);
                    setStartDate(yearStart.toISOString().split('T')[0]);
                    setEndDate(today.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-2 text-sm bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors font-medium"
                >
                  YTD
                </button>
              </div>
            </div>
            <div className="flex items-end justify-end gap-2">
              <button
                onClick={handleExportExcel}
                disabled={isExporting}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <TableCellsIcon className="h-5 w-5" />
                    Export Excel
                  </>
                )}
              </button>
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <DocumentTextIcon className="h-5 w-5" />
                    Export PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Total Cost */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <CurrencyDollarIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                API Cost
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(summaryStats.totalCost)}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Total enrichment costs
            </p>
          </div>

          {/* Total Saved */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CurrencyDollarIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Cache Savings
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(summaryStats.totalSaved)}
            </div>
            <p className="text-sm text-green-600 dark:text-green-400 mt-1">
              {summaryStats.savingsPercentage}% saved via caching
            </p>
          </div>

          {/* Total Enrichments */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <ChartBarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Enrichments
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatNumber(summaryStats.totalEnrichments)}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Total API calls
            </p>
          </div>

          {/* Active Users */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <UserGroupIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Active Users
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {summaryStats.activeUsers} / {summaryStats.totalUsers}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Users with activity
            </p>
          </div>
        </div>

        {/* User Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Table Header with Filters */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5 text-gray-500" />
                User Breakdown
                {selectedUsers.length > 0 && (
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({selectedUsers.length} selected)
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-48 pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {/* Active Filter */}
                <button
                  onClick={() => setShowOnlyActive(!showOnlyActive)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    showOnlyActive
                      ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <FunnelIcon className="h-4 w-4" />
                  Active Only
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredAndSortedUsers.length && filteredAndSortedUsers.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 dark:border-gray-600 text-orange-500 focus:ring-orange-500"
                    />
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-900 dark:hover:text-white"
                    onClick={() => handleSort('username')}
                  >
                    User <SortIcon field="username" />
                  </th>
                  <th
                    className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-900 dark:hover:text-white"
                    onClick={() => handleSort('total_enrichments')}
                  >
                    Enrichments <SortIcon field="total_enrichments" />
                  </th>
                  <th
                    className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-900 dark:hover:text-white"
                    onClick={() => handleSort('total_cost')}
                  >
                    API Cost <SortIcon field="total_cost" />
                  </th>
                  <th
                    className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-900 dark:hover:text-white"
                    onClick={() => handleSort('total_cost_saved')}
                  >
                    Cache Saved <SortIcon field="total_cost_saved" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredAndSortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      No users found matching your criteria
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedUsers.map((user) => (
                      <tr
                        key={user.id}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                          selectedUsers.includes(user.id) ? 'bg-orange-50 dark:bg-orange-900/10' : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => toggleUserSelection(user.id)}
                            className="rounded border-gray-300 dark:border-gray-600 text-orange-500 focus:ring-orange-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <button
                              onClick={() => handleUserClick(user)}
                              className="font-medium text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 hover:underline text-left"
                            >
                              {user.username}
                            </button>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {user.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-medium ${
                            user.stats.total_enrichments > 0
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-400 dark:text-gray-500'
                          }`}>
                            {formatNumber(user.stats.total_enrichments)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-medium ${
                            user.stats.total_cost > 0
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-gray-400 dark:text-gray-500'
                          }`}>
                            {formatCurrency(user.stats.total_cost)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-medium ${
                            user.stats.total_cost_saved > 0
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-gray-400 dark:text-gray-500'
                          }`}>
                            {formatCurrency(user.stats.total_cost_saved)}
                          </span>
                        </td>
                      </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
              <span>
                Showing {filteredAndSortedUsers.length} of {users.length} users
              </span>
              {selectedUsers.length > 0 && (
                <button
                  onClick={() => setSelectedUsers([])}
                  className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300"
                >
                  Clear selection
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <p className="font-medium mb-1">About Usage Statistics</p>
              <p className="text-blue-600 dark:text-blue-400">
                Data shown reflects the last 30 days of enrichment activity by default.
                API Cost represents actual charges from the enrichment service.
                Cache Saved shows costs avoided by using cached results.
              </p>
            </div>
          </div>
        </div>

        {/* User Detail Modal */}
        {selectedUser && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
                onClick={closeUserDetail}
              />

              {/* Modal */}
              <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Enrichment History
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedUser.username} ({selectedUser.email})
                      </p>
                    </div>
                    <button
                      onClick={closeUserDetail}
                      className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {/* Summary Stats */}
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Enrichments</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatNumber(selectedUser.stats?.total_enrichments || 0)}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">API Cost</div>
                      <div className="text-lg font-semibold text-red-600 dark:text-red-400">
                        {formatCurrency(selectedUser.stats?.total_cost || 0)}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Cache Saved</div>
                      <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {formatCurrency(selectedUser.stats?.total_cost_saved || 0)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transactions Table */}
                <div className="max-h-96 overflow-y-auto">
                  {loadingUserDetail ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                      <span className="ml-3 text-gray-500 dark:text-gray-400">Loading transactions...</span>
                    </div>
                  ) : userTransactions.length === 0 ? (
                    <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                      No enrichment transactions found for this period.
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                            Time
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                            Project
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                            API Cost
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                            Cache Saved
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {userTransactions.map((txn, index) => (
                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                              {txn.date}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                              {txn.time}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                              {txn.project_name || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              <span className={txn.cost > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}>
                                {formatCurrency(txn.cost || 0)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              <span className={txn.cost_saved > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                                {formatCurrency(txn.cost_saved || 0)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {userTransactions.length} transaction{userTransactions.length !== 1 ? 's' : ''} in selected period
                    </span>
                    <button
                      onClick={closeUserDetail}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Usage;
